// src/edu-world/ui/feedback-effects.ts
// Immediate positive feedback effects for all player actions.
// Provides visual animation within 500ms for mission acceptance, minigame completion,
// NPC interaction, and micro-interactions.
// All effects use soft colors, rounded shapes, playful proportions.
// NO negative consequences — only positive reinforcement.
// Requirements: 12.2, 12.3, 12.4, 12.5

import Phaser from 'phaser';

export type FeedbackType = 'mission_accept' | 'mission_complete' | 'npc_interact' | 'micro_interact';

/**
 * Shows immediate positive visual feedback at the given position.
 * Each type has a distinct but always positive effect:
 * - mission_accept: ⭐ star burst + scale pulse
 * - mission_complete: 🎉 confetti of colored soft circles
 * - npc_interact: 💫 small sparkle ring
 * - micro_interact: ✨ gentle sparkle pop
 *
 * All effects auto-destroy after 800ms.
 * All use soft colors, rounded shapes, no harsh contrasts.
 * Text is limited to max 5 words per label.
 */
export function showPositiveFeedback(
  scene: Phaser.Scene,
  position: { x: number; y: number },
  type: FeedbackType
): void {
  switch (type) {
    case 'mission_accept':
      playMissionAcceptEffect(scene, position);
      break;
    case 'mission_complete':
      playMissionCompleteEffect(scene, position);
      break;
    case 'npc_interact':
      playNpcInteractEffect(scene, position);
      break;
    case 'micro_interact':
      playMicroInteractEffect(scene, position);
      break;
  }
}

// ─── Soft color palette ───
// Warm, child-friendly colors with no harsh contrasts
const SOFT_COLORS = {
  gold: 0xffd54f,
  warmYellow: 0xfff176,
  peach: 0xffcc80,
  pink: 0xf48fb1,
  lavender: 0xce93d8,
  skyBlue: 0x81d4fa,
  mint: 0x80cbc4,
  coral: 0xef9a9a,
  cream: 0xfff9c4,
};

/**
 * Mission accept: Star burst + scale pulse.
 * A golden star expands from the center with radiating soft rays.
 */
function playMissionAcceptEffect(scene: Phaser.Scene, position: { x: number; y: number }): void {
  const { x, y } = position;
  const container = scene.add.container(x, y);
  container.setDepth(25);

  // Central star (golden circle with glow)
  const starCore = scene.add.circle(0, 0, 12, SOFT_COLORS.gold, 0.9);
  const starGlow = scene.add.circle(0, 0, 18, SOFT_COLORS.warmYellow, 0.4);
  container.add([starGlow, starCore]);

  // Radiating rays (6 small circles expanding outward)
  const rayCount = 6;
  const rays: Phaser.GameObjects.Arc[] = [];
  for (let i = 0; i < rayCount; i++) {
    const angle = (i / rayCount) * Math.PI * 2;
    const ray = scene.add.circle(0, 0, 5, SOFT_COLORS.peach, 0.7);
    container.add(ray);
    rays.push(ray);

    // Expand outward
    scene.tweens.add({
      targets: ray,
      x: Math.cos(angle) * 28,
      y: Math.sin(angle) * 28,
      scale: 0.3,
      alpha: 0,
      duration: 500,
      delay: 100 + i * 30,
      ease: 'Quad.easeOut',
    });
  }

  // Star scale pulse
  scene.tweens.add({
    targets: [starCore, starGlow],
    scale: { from: 0.3, to: 1.3 },
    duration: 300,
    ease: 'Back.easeOut',
    yoyo: true,
    hold: 100,
  });

  // ⭐ emoji overlay
  const starEmoji = scene.add.text(0, -20, '⭐', {
    fontSize: '18px',
  }).setOrigin(0.5);
  container.add(starEmoji);

  scene.tweens.add({
    targets: starEmoji,
    y: -35,
    alpha: { from: 1, to: 0 },
    scale: { from: 0.5, to: 1.2 },
    duration: 600,
    delay: 100,
    ease: 'Quad.easeOut',
  });

  // Auto-destroy after 800ms
  scene.time.delayedCall(800, () => {
    container.destroy(true);
  });
}

/**
 * Mission complete: Confetti of colored soft circles.
 * Multiple small circles pop up in random directions with soft colors.
 */
function playMissionCompleteEffect(scene: Phaser.Scene, position: { x: number; y: number }): void {
  const { x, y } = position;
  const container = scene.add.container(x, y);
  container.setDepth(25);

  const confettiColors = [
    SOFT_COLORS.pink,
    SOFT_COLORS.lavender,
    SOFT_COLORS.skyBlue,
    SOFT_COLORS.mint,
    SOFT_COLORS.peach,
    SOFT_COLORS.warmYellow,
    SOFT_COLORS.coral,
  ];

  // Generate 10 confetti pieces
  for (let i = 0; i < 10; i++) {
    const color = confettiColors[i % confettiColors.length];
    const size = Phaser.Math.Between(4, 8);
    const piece = scene.add.circle(0, 0, size, color, 0.85);
    container.add(piece);

    // Random direction
    const angle = Math.random() * Math.PI * 2;
    const distance = Phaser.Math.Between(25, 55);
    const targetX = Math.cos(angle) * distance;
    const targetY = Math.sin(angle) * distance - Phaser.Math.Between(10, 30); // slight upward bias

    scene.tweens.add({
      targets: piece,
      x: targetX,
      y: targetY,
      scale: { from: 0.2, to: 1 },
      alpha: { from: 1, to: 0 },
      duration: Phaser.Math.Between(500, 700),
      delay: i * 30,
      ease: 'Quad.easeOut',
    });
  }

  // 🎉 emoji
  const confettiEmoji = scene.add.text(0, -15, '🎉', {
    fontSize: '20px',
  }).setOrigin(0.5);
  container.add(confettiEmoji);

  scene.tweens.add({
    targets: confettiEmoji,
    y: -40,
    scale: { from: 0.3, to: 1.3 },
    alpha: { from: 1, to: 0 },
    duration: 700,
    delay: 50,
    ease: 'Back.easeOut',
  });

  // Auto-destroy after 800ms
  scene.time.delayedCall(800, () => {
    container.destroy(true);
  });
}

/**
 * NPC interact: Small sparkle ring.
 * A ring of gentle sparkles appears around the interaction point.
 */
function playNpcInteractEffect(scene: Phaser.Scene, position: { x: number; y: number }): void {
  const { x, y } = position;
  const container = scene.add.container(x, y);
  container.setDepth(25);

  // Ring of sparkle dots
  const sparkleCount = 5;
  for (let i = 0; i < sparkleCount; i++) {
    const angle = (i / sparkleCount) * Math.PI * 2;
    const radius = 16;
    const sparkle = scene.add.circle(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
      3,
      SOFT_COLORS.cream,
      0.9
    );
    container.add(sparkle);

    // Pulse and fade
    scene.tweens.add({
      targets: sparkle,
      scale: { from: 0.5, to: 1.5 },
      alpha: { from: 0.9, to: 0 },
      x: Math.cos(angle) * (radius + 8),
      y: Math.sin(angle) * (radius + 8),
      duration: 500,
      delay: i * 50,
      ease: 'Sine.easeOut',
    });
  }

  // 💫 emoji
  const sparkEmoji = scene.add.text(0, -14, '💫', {
    fontSize: '14px',
  }).setOrigin(0.5);
  container.add(sparkEmoji);

  scene.tweens.add({
    targets: sparkEmoji,
    y: -24,
    alpha: { from: 1, to: 0 },
    scale: { from: 0.5, to: 1.1 },
    duration: 600,
    delay: 50,
    ease: 'Quad.easeOut',
  });

  // Auto-destroy after 800ms
  scene.time.delayedCall(800, () => {
    container.destroy(true);
  });
}

/**
 * Micro-interact: Gentle sparkle pop.
 * A quick twinkle of soft sparkles.
 */
function playMicroInteractEffect(scene: Phaser.Scene, position: { x: number; y: number }): void {
  const { x, y } = position;
  const container = scene.add.container(x, y);
  container.setDepth(25);

  // 3 sparkle dots that pop outward
  const sparklePositions = [
    { angle: -Math.PI / 3, dist: 12 },
    { angle: Math.PI / 6, dist: 15 },
    { angle: Math.PI * 0.8, dist: 10 },
  ];

  sparklePositions.forEach(({ angle, dist }, i) => {
    const sparkle = scene.add.circle(0, 0, 4, SOFT_COLORS.warmYellow, 0.85);
    container.add(sparkle);

    scene.tweens.add({
      targets: sparkle,
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist,
      scale: { from: 0.3, to: 1.2 },
      alpha: { from: 0.85, to: 0 },
      duration: 450,
      delay: i * 60,
      ease: 'Quad.easeOut',
    });
  });

  // ✨ emoji (small)
  const twinkleEmoji = scene.add.text(0, -10, '✨', {
    fontSize: '12px',
  }).setOrigin(0.5);
  container.add(twinkleEmoji);

  scene.tweens.add({
    targets: twinkleEmoji,
    y: -20,
    alpha: { from: 1, to: 0 },
    scale: { from: 0.4, to: 1 },
    duration: 500,
    delay: 30,
    ease: 'Quad.easeOut',
  });

  // Auto-destroy after 800ms
  scene.time.delayedCall(800, () => {
    container.destroy(true);
  });
}
